import React, { useMemo } from "react";

import { Alert, Box, TextField } from "@mui/material";

import {

  EditorSection,

  EditorSubsection,

  EditorFormNav,

  EditorListItem,

  EditorListHeader,

  ImageFieldsEditor,

  LinkListEditor,

  StringListEditor,

} from "./institutional/InstitutionalEditorParts";

import { SectionsWithBlocksEditor, VariantSpecificPanels } from "./institutional/InstitutionalVariantPanels";

import InstitutionalIconPicker from "./shared/InstitutionalIconPicker";



const emptyCard = () => ({ title: "", summary: "", bullets: [] });

const emptySection = () => ({

  id: "",

  title: "",

  eyebrow: "",

  summary: "",

  highlights: [],

  blocks: [],

  links: [],

  warning: "",

});

const emptyFaq = () => ({ question: "", answer: "" });

const emptyTextSection = () => ({ id: "", eyebrow: "", title: "", description: "", body: "", paragraphs: [] });



const LIGHT_VARIANTS = new Set([

  "formas_pagamento",

  "encomendas",

  "como_comprar",

  "acessibilidade",

  "elas",

]);



const VARIANT_LABELS = {

  sobre: "Sobre a empresa",

  informacoes: "Informações ao passageiro",

  beneficios: "Benefícios",

  sustentabilidade: "Sustentabilidade",

  vale_transporte: "Vale transporte",

  trabalhe_conosco: "Trabalhe conosco",

  formas_pagamento: "Formas de pagamento",

  encomendas: "Encomendas",

  como_comprar: "Como comprar",

  acessibilidade: "Acessibilidade",

  elas: "Elas",

};



export default function PaginaInstitucionalEditor({ value, onChange }) {

  const v = value?.format === "pagina_institucional.v1" ? value : { format: "pagina_institucional.v1", variant: "informacoes" };

  const set = (patch) => onChange({ ...v, ...patch });

  const setHero = (patch) => set({ hero: { ...(v.hero || {}), ...patch } });

  const setMeta = (patch) => set({ meta: { ...(v.meta || {}), ...patch } });

  const setFaq = (patch) => set({ faq: { ...(v.faq || {}), ...patch } });

  const setClosing = (patch) => set({ closingCta: { ...(v.closingCta || {}), ...patch } });



  const cards = Array.isArray(v.cards) ? v.cards : [];

  const sections = Array.isArray(v.sections) ? v.sections : [];

  const textSections = Array.isArray(v.textSections) ? v.textSections : [];

  const faqItems = Array.isArray(v.faq?.items) ? v.faq.items : [];

  const isLight = LIGHT_VARIANTS.has(v.variant);

  const showCards = !isLight || v.variant === "elas";

  const showAccordion = !isLight && !["sustentabilidade", "vale_transporte", "trabalhe_conosco", "elas"].includes(v.variant);

  const showClosing = !["beneficios", "informacoes", "acessibilidade"].includes(v.variant);

  const showFaq = isLight || faqItems.length > 0;

  const variantLabel = VARIANT_LABELS[v.variant] || v.variant;



  const navSections = useMemo(() => {

    const items = [

      { id: "sec-google", label: "Google" },

      { id: "sec-abertura", label: "Abertura" },

    ];

    if (showCards) items.push({ id: "sec-cards", label: `Cards (${cards.length})` });

    items.push({ id: "sec-textos", label: `Textos (${textSections.length})` });

    if (showAccordion) items.push({ id: "sec-acordeao", label: `Acordeão (${sections.length})` });

    if (v.variant === "sobre") items.push({ id: "sec-especifico", label: "Conteúdo da Sobre" });

    if (["informacoes", "acessibilidade", "beneficios", "sustentabilidade", "vale_transporte", "trabalhe_conosco"].includes(v.variant)) {

      items.push({ id: "sec-especifico", label: "Conteúdo extra" });

    }

    if (showFaq) items.push({ id: "sec-faq", label: `FAQ (${faqItems.length})` });

    if (showClosing) items.push({ id: "sec-final", label: "Chamada final" });

    return items;

  }, [showCards, cards.length, textSections.length, showAccordion, sections.length, v.variant, showFaq, faqItems.length, showClosing]);



  return (

    <Box sx={{ mt: 0.5 }}>

      <Alert severity="info" sx={{ mb: 2 }}>

        Página: <strong>{variantLabel}</strong>. Abra uma seção por vez — o site monta tudo automaticamente, sem código.

      </Alert>



      <EditorFormNav sections={navSections} />



      <EditorSection

        id="sec-google"

        title="Como a página aparece no Google"

        subtitle="Título e descrição nos resultados de busca e ao compartilhar"

        optional

        defaultExpanded={false}

      >

        <TextField fullWidth label="Título da página" value={v.meta?.title || ""} onChange={(e) => setMeta({ title: e.target.value })} helperText="Aparece na aba do navegador e nos resultados de busca." sx={{ mb: 2 }} />

        <TextField fullWidth multiline minRows={2} label="Descrição curta" value={v.meta?.description || ""} onChange={(e) => setMeta({ description: e.target.value })} helperText="Resumo de uma ou duas frases para o Google." sx={{ mb: 2 }} />

        <TextField fullWidth multiline minRows={2} label="Descrição ao compartilhar" value={v.meta?.openGraphDescription || ""} onChange={(e) => setMeta({ openGraphDescription: e.target.value })} helperText="Usada no WhatsApp e redes sociais." sx={{ mb: 2 }} />

        <TextField fullWidth size="small" label="Texto do botão Voltar" value={v.backButtonLabel || ""} onChange={(e) => set({ backButtonLabel: e.target.value })} />

      </EditorSection>



      <EditorSection

        id="sec-abertura"

        title="Abertura da página"

        subtitle="Banner grande no topo — é a primeira coisa que o visitante vê"

        defaultExpanded

      >

        <EditorSubsection title="Textos principais" description="Título, texto de apoio e observações.">

          <TextField fullWidth label="Etiqueta pequena (acima do título)" value={v.hero?.eyebrow || ""} onChange={(e) => setHero({ eyebrow: e.target.value })} sx={{ mb: 2 }} />

          <TextField fullWidth multiline minRows={2} label="Título principal" value={v.hero?.title || ""} onChange={(e) => setHero({ title: e.target.value })} sx={{ mb: 2 }} />

          <TextField fullWidth multiline minRows={3} label="Texto de apoio" value={v.hero?.subtitle || ""} onChange={(e) => setHero({ subtitle: e.target.value })} sx={{ mb: 2 }} />

          <TextField fullWidth multiline minRows={2} label="Observação" value={v.hero?.note || ""} onChange={(e) => setHero({ note: e.target.value })} helperText="Aviso curto abaixo do texto principal." />

        </EditorSubsection>



        <EditorSubsection title="Foto, botões e atalhos" description="Imagem de destaque e links de ação no banner.">

          <ImageFieldsEditor

            image={v.hero?.image}

            imageAlt={v.hero?.imageAlt}

            label="Foto de abertura"

            variant="hero"

            uploadSubpath={`contents/institutional/${v.variant || "page"}`}

            onChange={({ image, imageAlt }) => setHero({ image, imageAlt })}

          />

          <LinkListEditor label="Botões principais" items={v.hero?.ctas} onChange={(ctas) => setHero({ ctas })} />

          <LinkListEditor label="Atalhos no topo" items={v.hero?.chips} onChange={(chips) => setHero({ chips })} />

        </EditorSubsection>



        <EditorSubsection

          title="Coluna lateral"

          description="Resumo rápido exibido ao lado do banner em telas grandes."

          optional

        >

          <TextField fullWidth label="Título da coluna lateral" value={v.hero?.sidebarTitle || ""} onChange={(e) => setHero({ sidebarTitle: e.target.value })} sx={{ mb: 2 }} />

          <StringListEditor label="Destaques laterais" items={v.hero?.highlights} onChange={(highlights) => setHero({ highlights })} />

          <StringListEditor label="Passos laterais" items={v.hero?.sidebarSteps} onChange={(sidebarSteps) => setHero({ sidebarSteps })} />

          <TextField fullWidth multiline minRows={2} label="Nota lateral" value={v.hero?.sidebarNote || ""} onChange={(e) => setHero({ sidebarNote: e.target.value })} sx={{ mt: 1 }} />

        </EditorSubsection>

      </EditorSection>



      {showCards ? (

        <EditorSection

          id="sec-cards"

          title="Cards de resumo"

          subtitle="Destaques em cartões logo abaixo do banner"

          badge={cards.length || null}

          defaultExpanded={cards.length > 0}

        >

          <EditorListHeader

            description="Cada card mostra um ícone, título e resumo. Clique no card para editar."

            onAdd={() => set({ cards: [...cards, emptyCard()] })}

            addLabel="+ Adicionar card"

          />

          {cards.map((card, ci) => (

            <EditorListItem

              key={ci}

              index={ci}

              title={card.title ? `Card ${ci + 1}: ${card.title}` : `Card ${ci + 1}`}

              summary={card.summary || card.desc}

              defaultExpanded={ci === cards.length - 1}

              onDelete={() => set({ cards: cards.filter((_, i) => i !== ci) })}

            >

              <TextField fullWidth size="small" label="Identificador interno" value={card.id || ""} onChange={(e) => { const next = [...cards]; next[ci] = { ...next[ci], id: e.target.value }; set({ cards: next }); }} helperText="Só para atalhos na mesma página. Ex.: bagagem" sx={{ mb: 1 }} />

              <InstitutionalIconPicker

                value={card.icon || ""}

                onChange={(icon) => { const next = [...cards]; next[ci] = { ...next[ci], icon }; set({ cards: next }); }}

              />

              <TextField fullWidth size="small" label="Título" value={card.title || ""} onChange={(e) => { const next = [...cards]; next[ci] = { ...next[ci], title: e.target.value }; set({ cards: next }); }} sx={{ mb: 1 }} />

              <TextField fullWidth size="small" multiline minRows={2} label="Resumo" value={card.summary || card.desc || ""} onChange={(e) => { const next = [...cards]; next[ci] = { ...next[ci], summary: e.target.value }; set({ cards: next }); }} sx={{ mb: 1 }} />

              <StringListEditor label="Tópicos" items={card.bullets} onChange={(bullets) => { const next = [...cards]; next[ci] = { ...next[ci], bullets }; set({ cards: next }); }} />

            </EditorListItem>

          ))}

        </EditorSection>

      ) : null}



      <EditorSection

        id="sec-textos"

        title="Blocos de texto"

        subtitle="Parágrafos com título, organizados em sequência na página"

        badge={textSections.length || null}

        defaultExpanded={textSections.length > 0}

      >

        <EditorListHeader

          description="Use para explicar regras, orientações ou histórias em blocos separados."

          onAdd={() => set({ textSections: [...textSections, emptyTextSection()] })}

          addLabel="+ Adicionar bloco"

        />

        {textSections.map((sec, si) => (

          <EditorListItem

            key={si}

            index={si}

            title={sec.title ? `Bloco ${si + 1}: ${sec.title}` : `Bloco ${si + 1}`}

            summary={sec.description || sec.body}

            defaultExpanded={si === textSections.length - 1}

            onDelete={() => set({ textSections: textSections.filter((_, i) => i !== si) })}

          >

            <TextField fullWidth size="small" label="Identificador interno" value={sec.id || ""} onChange={(e) => { const next = [...textSections]; next[si] = { ...next[si], id: e.target.value }; set({ textSections: next }); }} helperText="Para links que levam a este bloco." sx={{ mb: 1 }} />

            <TextField fullWidth size="small" label="Etiqueta" value={sec.eyebrow || ""} onChange={(e) => { const next = [...textSections]; next[si] = { ...next[si], eyebrow: e.target.value }; set({ textSections: next }); }} sx={{ mb: 1 }} />

            <TextField fullWidth size="small" label="Título" value={sec.title || ""} onChange={(e) => { const next = [...textSections]; next[si] = { ...next[si], title: e.target.value }; set({ textSections: next }); }} sx={{ mb: 1 }} />

            <TextField fullWidth multiline minRows={3} label="Texto" value={sec.description || sec.body || ""} onChange={(e) => { const next = [...textSections]; next[si] = { ...next[si], description: e.target.value }; set({ textSections: next }); }} />

          </EditorListItem>

        ))}

      </EditorSection>



      {showAccordion ? (

        <EditorSection

          id="sec-acordeao"

          title="Seções expansíveis"

          subtitle="Tópicos que o visitante abre e fecha na página (acordeão)"

          badge={sections.length || null}

          defaultExpanded={false}

        >

          <EditorListHeader

            description="Ideal para regras detalhadas, listas e links por assunto."

            onAdd={() => set({ sections: [...sections, emptySection()] })}

            addLabel="+ Adicionar seção"

          />

          {sections.map((sec, si) => (

            <EditorListItem

              key={si}

              index={si}

              title={sec.title ? `Seção ${si + 1}: ${sec.title}` : `Seção ${si + 1}`}

              summary={sec.summary}

              defaultExpanded={si === sections.length - 1}

              onDelete={() => set({ sections: sections.filter((_, i) => i !== si) })}

            >

              <TextField fullWidth size="small" label="Identificador interno" value={sec.id || ""} onChange={(e) => { const next = [...sections]; next[si] = { ...next[si], id: e.target.value }; set({ sections: next }); }} helperText="Para atalhos que abrem esta seção." sx={{ mb: 1 }} />

              <TextField fullWidth size="small" label="Etiqueta" value={sec.eyebrow || ""} onChange={(e) => { const next = [...sections]; next[si] = { ...next[si], eyebrow: e.target.value }; set({ sections: next }); }} sx={{ mb: 1 }} />

              <TextField fullWidth size="small" label="Título" value={sec.title || ""} onChange={(e) => { const next = [...sections]; next[si] = { ...next[si], title: e.target.value }; set({ sections: next }); }} sx={{ mb: 1 }} />

              <TextField fullWidth multiline minRows={2} label="Resumo" value={sec.summary || ""} onChange={(e) => { const next = [...sections]; next[si] = { ...next[si], summary: e.target.value }; set({ sections: next }); }} sx={{ mb: 1 }} />

              <StringListEditor label="Destaques" items={sec.highlights} onChange={(highlights) => { const next = [...sections]; next[si] = { ...next[si], highlights }; set({ sections: next }); }} />

              <TextField fullWidth multiline minRows={2} label="Aviso" value={sec.warning || ""} onChange={(e) => { const next = [...sections]; next[si] = { ...next[si], warning: e.target.value }; set({ sections: next }); }} sx={{ mt: 1 }} />

            </EditorListItem>

          ))}

          <SectionsWithBlocksEditor sections={sections} onChange={(next) => set({ sections: next })} />

        </EditorSection>

      ) : null}



      <Box id="sec-especifico">

        <VariantSpecificPanels v={v} set={set} />

      </Box>



      {showFaq ? (

        <EditorSection

          id="sec-faq"

          title="Perguntas frequentes"

          subtitle="Perguntas e respostas no final da página"

          badge={faqItems.length || null}

          defaultExpanded={faqItems.length > 0}

        >

          <TextField fullWidth label="Título do bloco de perguntas" value={v.faq?.title || ""} onChange={(e) => setFaq({ title: e.target.value })} sx={{ mb: 2 }} />

          <EditorListHeader

            onAdd={() => setFaq({ items: [...faqItems, emptyFaq()] })}

            addLabel="+ Adicionar pergunta"

          />

          {faqItems.map((item, fi) => (

            <EditorListItem

              key={fi}

              index={fi}

              title={item.question ? `Pergunta ${fi + 1}` : `Pergunta ${fi + 1}`}

              summary={item.question}

              defaultExpanded={fi === faqItems.length - 1}

              onDelete={() => setFaq({ items: faqItems.filter((_, i) => i !== fi) })}

            >

              <TextField fullWidth size="small" label="Pergunta" value={item.question || ""} onChange={(e) => { const next = [...faqItems]; next[fi] = { ...next[fi], question: e.target.value }; setFaq({ items: next }); }} sx={{ mb: 1 }} />

              <TextField fullWidth multiline minRows={2} label="Resposta" value={item.answer || ""} onChange={(e) => { const next = [...faqItems]; next[fi] = { ...next[fi], answer: e.target.value }; setFaq({ items: next }); }} />

            </EditorListItem>

          ))}

        </EditorSection>

      ) : null}



      {showClosing ? (

        <EditorSection

          id="sec-final"

          title="Chamada final"

          subtitle="Convite para ação no rodapé da página"

          defaultExpanded={false}

        >

          <TextField fullWidth label="Título" value={v.closingCta?.title || ""} onChange={(e) => setClosing({ title: e.target.value })} sx={{ mb: 2 }} />

          <TextField fullWidth multiline minRows={2} label="Texto" value={v.closingCta?.description || ""} onChange={(e) => setClosing({ description: e.target.value })} sx={{ mb: 2 }} />

          <LinkListEditor label="Botões" items={v.closingCta?.ctas} onChange={(ctas) => setClosing({ ctas })} />

        </EditorSection>

      ) : null}

    </Box>

  );

}


